import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    LoopPositionExpanded,
    StrategyExpandedInfo,
    AbfLoanExpanded,
    LoanCollateralExpanded
} from "@bridgesplit/abf-react";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

export enum ManageLeverageAction {
    IncreaseLeverage,
    DecreaseLeverage,
    NoAction
}

export interface NewDebtAndCollateralAmounts {
    newDebtAmount: number | undefined;
    newDebtUsd: number | undefined;
    newCollateralAmount: number | undefined;
    newCollateralUsd: number | undefined;
    newHealth: number | undefined;
}

// param types
export interface UseFullRepayWithCollateralQuoteParams {
    loanExpanded: AbfLoanExpanded | undefined;
    slippagePercentDecimals: number | undefined;
}
export interface UseManageLeverageQuotesParams {
    action: ManageLeverageAction;
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    slippagePercentDecimals: number | undefined;
    increasePrincipalAmount: number | undefined;
    repayWithCollateralAmount: number | undefined;
}

export interface UseRepayWithCollateralQuoteParams {
    loanExpanded: AbfLoanExpanded | undefined;
    repayWithCollateralAmount: number | undefined;
    slippagePercentDecimals: number | undefined;
    isClosingLoop?: boolean;
    skip: boolean;
}
export interface UseIncreaseLeverageQuoteParams {
    loopPosition: LoopPositionExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    increasePrincipalAmount: number | undefined;
    slippagePercentDecimals: number | undefined;
    skip?: boolean;
}

export interface ExecutionDetailsInputsParams {
    increaseLeverageQuote: SwapInstructionsResponse | undefined;
    repayWithCollateralQuote: SwapInstructionsResponse | undefined;
    repayWithCollateralAmount: number | undefined;
    collateral: LoanCollateralExpanded | undefined;
    increasePrincipalAmount: number | undefined;
    isIncreaseLeverage: boolean;
    principalUsdPrice: number | undefined;
    collateralUsdPrice: number | undefined;
    principalMetadata: TokenListMetadata | undefined;
}

// Return types
export interface UseRepayWithCollateralQuoteReturn {
    repayWithCollateralQuote: SwapInstructionsResponse | undefined;
    repayWithCollateralQuoteLoading: boolean;
    repayWithCollateralQuoteFetching: boolean;
    repayWithCollateralQuoteError: FetchBaseQueryError | SerializedError | undefined;
    customRepayAmountInCollateral: number;
    customRepayAmountInPrincipalFromCollateralRepayment: number;
    validHealthRepayQuote: boolean;
}

export interface UseIncreaseLeverageQuoteReturn {
    increaseLeverageQuote: SwapInstructionsResponse | undefined;
    increaseLeverageQuoteLoading: boolean;
    increaseLeverageQuoteFetching: boolean;
    increaseLeverageQuoteError: FetchBaseQueryError | SerializedError | undefined;
    validHealthIncreaseLeverageQuote: boolean;
}

export interface UseManageLeverageQuotesReturn {
    quote: SwapInstructionsResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: FetchBaseQueryError | SerializedError | undefined | boolean;
    validHealth: boolean;
    action: ManageLeverageAction;
}

export interface UseFullRepayWithCollateralQuoteReturn {
    fullRepayWithCollateralQuote: SwapInstructionsResponse | undefined;
    fullRepayWithCollateralQuoteLoading: boolean;
    fullRepayWithCollateralQuoteError: boolean;
    fullRepayAmountCollateralUi: number;
    fullRepayAmountInPrincipalFromCollateralRepaymentUi: number;
}

export interface ExecutionDetailsInputs {
    externalQuote: SwapInstructionsResponse | undefined;
    receiveUsdAmount: number | undefined;
    fairMarketValue: number | undefined;
    displayTokenType: WindType | undefined;
}

interface CurrentStrategyLeverageCapacity {
    maxAvailableLeverage: number;
    increaseLeverageApy: number | undefined;
    increaseLeverageLiquidationThreshold: number | undefined;
    strategyCurrentLtv: number | undefined;
    strategy: StrategyExpandedInfo;
}

export interface UseCurrentStrategyLeverageCapacityResult {
    data: CurrentStrategyLeverageCapacity | undefined;
    isLoading: boolean;
}
